import { useState, useEffect, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faPlus,
  faList,
  faLayerGroup,
  faSave,
  faUsers,
  faUserCheck,
  faUserSlash,
  faSpinner,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import {
  statusStyles,
  statusDotStyles,
  formatDateTime,
} from "../../../utils/styles";
import Modal from "../../../components/ui/modal";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { Input } from "../../../components/ui/input";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import DateFilter from "../../../components/ui/DateFilter";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const ProductCategory = ({
  productCategories,
  productCategoriesLoading,
  productCategoriesError,
  productCategoriesPagination,
  productCategoriesPage,
  setProductCategoriesPage,
  productCategoriesPageSize,
  setProductCategoriesPageSize,
  productCategoriesSearch,
  setProductCategoriesSearch,
  productTypes,
  productTypesLoading,
  createProductCategory,
  updateProductCategory,
  toggleProductCategoryStatus,
  refreshProductCategories,
}) => {
  const { hasPermission } = useAuth();
  const canManageCategory = hasPermission([
    "manage_product_category",
    "manage_product",
  ]);

  const safeCategories = useMemo(
    () => (Array.isArray(productCategories) ? productCategories : []),
    [productCategories],
  );
  const safeProductTypes = useMemo(
    () => (Array.isArray(productTypes) ? productTypes : []),
    [productTypes],
  );

  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState({
    category_name: "",
    product_type_id: "",
    status: "active",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getTypeName = useMemo(() => {
    return (category) => category?.product_type?.name || "-";
  }, []);

  useEffect(() => {
    if (dialogOpen) {
      if (editingCategory) {
        const cat = editingCategory.product_category || editingCategory;
        setForm({
          category_name: cat.category_name || cat.name || "",
          product_type_id:
            cat.product_type?.productTypeId ||
            cat.productTypeId ||
            cat.product_type_id ||
            "",
          status: cat.status || "active",
        });
      } else {
        setForm({ category_name: "", product_type_id: "", status: "active" });
      }
      setErrors({});
    }
  }, [dialogOpen, editingCategory]);

  const filteredCategories = useMemo(() => {
    let result = safeCategories;
    if (statusFilter !== "all") {
      result = result.filter(
        (category) => category.status?.toLowerCase() === statusFilter,
      );
    }
    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((c) => {
        const createdDate = new Date(c.created_at);
        return createdDate >= start && createdDate <= end;
      });
    }
    return result;
  }, [safeCategories, statusFilter, startDate, endDate, isFilterApplied]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = async () => {
    if (!canManageCategory) return;
    const newErrors = {};
    if (!form.category_name.trim())
      newErrors.category_name = "Category name is required";
    if (!form.product_type_id)
      newErrors.product_type_id = "Product type is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      setIsSubmitting(true);
      const data = {
        category_name: form.category_name,
        product_type_id: form.product_type_id,
        status: form.status,
      };
      if (editingCategory) {
        await updateProductCategory(editingCategory.productCategoryId, data);
        toastSuccess("Product category updated successfully!");
      } else {
        await createProductCategory(data);
        toastSuccess("Product category created successfully!");
      }
      setDialogOpen(false);
      setEditingCategory(null);
      setForm({ category_name: "", product_type_id: "", status: "active" });
      setErrors({});
      await refreshProductCategories();
    } catch (error) {
      toastError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save product category",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (productCategoryId, currentStatus) => {
    if (!canManageCategory) return;
    try {
      await toggleProductCategoryStatus(productCategoryId);
      toastSuccess(
        `Product category ${currentStatus === "active" ? "deactivated" : "activated"} successfully!`,
      );
      await refreshProductCategories();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update status");
    }
  };

  const statusTabs = [
    { id: "all", label: "All", icon: faUsers },
    { id: "active", label: "Active", icon: faUserCheck },
    { id: "inactive", label: "Inactive", icon: faUserSlash },
  ];

  const tabCounts = {
    all: productCategoriesPagination?.total ?? safeCategories.length,
    active: safeCategories.filter(
      (c) => c.status === "active" || c.status === "ACTIVE",
    ).length,
    inactive: safeCategories.filter(
      (c) => c.status === "inactive" || c.status === "INACTIVE",
    ).length,
  };

  const totalItems =
    productCategoriesPagination?.total ?? safeCategories.length;
  const totalPages = productCategoriesPagination?.last_page ?? 1;

  if (productCategoriesLoading && safeCategories.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-8 w-8 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading product categories...</p>
        </div>
      </div>
    );
  }

  if (productCategoriesError) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-600">
          Failed to load product categories. Please try again.
        </p>
        <Button
          onClick={() => refreshProductCategories()}
          className="ml-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Product Categories
          </h3>
          <p className="text-sm text-gray-600">
            Manage product categories ({totalItems} total)
          </p>
        </div>
        {canManageCategory && (
          <Button
            onClick={() => {
              setEditingCategory(null);
              setForm({
                category_name: "",
                product_type_id: "",
                status: "active",
              });
              setErrors({});
              setDialogOpen(true);
            }}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add category
          </Button>
        )}
      </div>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setProductCategoriesPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setProductCategoriesPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={filteredCategories.length}
        label="categories"
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={productCategoriesSearch}
          onChange={(value) => {
            setProductCategoriesSearch(value);
            setProductCategoriesPage(1);
          }}
          placeholder="Search by category name..."
          className="flex-1"
        />

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {statusTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setStatusFilter(tab.id);
                setProductCategoriesPage(1);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                statusFilter === tab.id
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="h-3 w-3" />
              <span>{tab.label}</span>
              <span
                className={`ml-0.5 px-1.5 py-0.5 text-xs rounded-full ${
                  statusFilter === tab.id
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {tabCounts[tab.id]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <TableWrapper
        title="items"
        totalItems={totalItems}
        currentPage={productCategoriesPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                S/N
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Category Name
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Product Type
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Status
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Created At
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Updated At
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 text-center">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((item, i) => {
                const status = item.status?.toUpperCase() || "ACTIVE";
                const typeName = getTypeName(item);
                const rowNumber =
                  (productCategoriesPage - 1) * productCategoriesPageSize +
                  i +
                  1;
                return (
                  <TableRow
                    key={item.productCategoryId || item.id || i}
                    className="hover:bg-blue-50/50 transition-colors border-b border-gray-100"
                  >
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {rowNumber}
                    </TableCell>
                    <TableCell className="py-3 px-4 font-medium text-gray-900 text-sm">
                      {item.category_name || item.name || "N/A"}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      <Badge
                        variant="outline"
                        className="bg-purple-50 text-purple-700 border-purple-200 font-normal px-3 py-1 rounded-full text-xs"
                      >
                        {typeName}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${statusStyles[status] || "bg-gray-100 text-gray-700"} border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${statusDotStyles[status] || "bg-gray-400"}`}
                        />
                        {status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateTime(item.created_at)}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateTime(item.updated_at)}
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="flex gap-2 justify-center">
                        {canManageCategory ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingCategory(item);
                                setDialogOpen(true);
                              }}
                              className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50"
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="h-3 w-3"
                              />{" "}
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleToggleStatus(
                                  item.productCategoryId || item.id,
                                  item.status,
                                )
                              }
                              className={`gap-1 ${
                                item.status === "active" ||
                                item.status === "ACTIVE"
                                  ? "border-orange-500 text-orange-600 hover:bg-orange-50"
                                  : "border-green-500 text-green-600 hover:bg-green-50"
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={
                                  item.status === "active" ||
                                  item.status === "ACTIVE"
                                    ? faToggleOff
                                    : faToggleOn
                                }
                                className="h-3 w-3"
                              />
                              {item.status === "active" ||
                              item.status === "ACTIVE"
                                ? "Deactivate"
                                : "Activate"}
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <EmptyState
                    title="No categories found"
                    message={
                      productCategoriesSearch || isFilterApplied
                        ? "Try adjusting your search or filters"
                        : "Add your first category to get started"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={productCategoriesPage}
          totalPages={totalPages}
          pageSize={productCategoriesPageSize}
          totalItems={totalItems}
          onPageChange={setProductCategoriesPage}
          onPageSizeChange={setProductCategoriesPageSize}
          pageSizeOptions={[5, 10, 25, 50, 100]}
        />
      )}

      <Modal
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingCategory(null);
          setErrors({});
        }}
        title={
          editingCategory ? "Edit product category" : "Add new product category"
        }
        footer={
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                setEditingCategory(null);
                setErrors({});
              }}
              disabled={isSubmitting}
              className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting}
              className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="h-4 w-4 animate-spin"
                  />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  {editingCategory ? "Update" : "Save"}
                </>
              )}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faList}
                className="text-blue-800 h-4 w-4"
              />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-semibold">
                {editingCategory
                  ? "Edit product category"
                  : "Create new product category"}
              </p>
              <p className="text-xs text-gray-600">
                Fill in all required fields (*)
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faList}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Category name *
            </label>
            <Input
              name="category_name"
              value={form.category_name}
              onChange={handleChange}
              placeholder="Enter category name"
              disabled={isSubmitting}
              className={`border ${errors.category_name ? "border-red-500" : "border-gray-300"} rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
            />
            {errors.category_name && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.category_name}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faLayerGroup}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Product type *
            </label>
            <Select
              value={form.product_type_id}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, product_type_id: value }));
                if (errors.product_type_id) {
                  setErrors((prev) => ({ ...prev, product_type_id: "" }));
                }
              }}
              disabled={isSubmitting || productTypesLoading}
            >
              <SelectTrigger className="w-full h-10 border-gray-300">
                <SelectValue
                  placeholder={
                    productTypesLoading
                      ? "Loading product types..."
                      : "Select product type"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {safeProductTypes.filter((t) => t.status === "active").length >
                0 ? (
                  safeProductTypes
                    .filter((t) => t.status === "active")
                    .map((t) => (
                      <SelectItem key={t.productTypeId} value={t.productTypeId}>
                        {t.name}
                      </SelectItem>
                    ))
                ) : (
                  <SelectItem value="__none__" disabled>
                    No product types available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {errors.product_type_id && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.product_type_id}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium">Status</label>
            <Select
              value={form.status}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, status: value }))
              }
              disabled={isSubmitting}
            >
              <SelectTrigger className="w-full h-10 border-gray-300">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProductCategory;
